import { XmlImportActions } from '@store/xmlImportSaga'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { selectIsLoading } from '@/store'
import { parseXmlImportModel, XML_PARSER_ERROR } from '@/utils/xml'
import { FILE_TYPE_ERROR } from './constants'
import type { UseXmlImportProps } from './types'
import { isXmlFile, readFileAsText } from './utils'

export const useXmlImport = ({ teamId }: UseXmlImportProps) => {
  const dispatch = useDispatch()
  const [file, setFile] = useState<File | null>(null)
  const loading = useSelector(selectIsLoading('project_save', 'project_create'))
  const [error, setError] = useState('')

  const selectFile = (nextFile: File) => {
    if (!isXmlFile(nextFile)) {
      setFile(null)
      setError(FILE_TYPE_ERROR)
      sendGtmEvent(GtmTriggerName.IMPORT_FILE_ERROR)
      return
    }

    setFile(nextFile)
    setError('')
  }

  const clearFile = () => {
    setFile(null)
    setError('')
  }

  const importXml = async () => {
    if (!file || loading) {
      return
    }

    try {
      const payload = await readFileAsText(file)
      const model = parseXmlImportModel(payload)

      sendGtmEvent(GtmTriggerName.IMPORT_FILE_SUCCESS)

      dispatch(
        XmlImportActions.import({
          model,
          teamId: teamId || null,
        })
      )
    } catch {
      setError(XML_PARSER_ERROR)
      sendGtmEvent(GtmTriggerName.IMPORT_FILE_ERROR_2)
    }
  }

  return {
    file,
    loading,
    error,
    selectFile,
    clearFile,
    importXml,
  }
}
